/*
 * Copyright (C) 2024 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {create} from 'zustand'
import type {AccountId} from '../model/AccountId'
import type {DynamicRegistrationToken} from '../model/DynamicRegistrationToken'
import {
  containsPlacementWithIcon,
  createLti1p3RegistrationOverlayStore,
  type Lti1p3RegistrationOverlayStore,
} from '../registration_overlay/Lti1p3RegistrationOverlayStore'
import {convertToLtiConfigurationOverlay} from '../registration_overlay/Lti1p3RegistrationOverlayStateHelpers'
import type {DynamicRegistrationWizardService} from './DynamicRegistrationWizardService'
import {
  formatApiResultError,
  type ApiResult,
  isUnsuccessful,
  isSuccessful,
  combineAllApiResults,
} from '../../common/lib/apiResult/ApiResult'
import type {LtiRegistrationId} from '../model/LtiRegistrationId'
import type {UnifiedToolId} from '../model/UnifiedToolId'
import type {LtiRegistrationWithConfiguration} from '../model/LtiRegistration'
import type {LtiConfigurationOverlay} from '../model/internal_lti_configuration/LtiConfigurationOverlay'
import {isLtiPlacementWithIcon, LtiPlacementsWithIcons} from '../model/LtiPlacement'
import {filterPlacementsByFeatureFlags} from '@canvas/lti/model/LtiPlacementFilter'
import {
  validateIconUris,
  getInputIdForField,
  Lti1p3RegistrationOverlayStateErrorField,
  Lti1p3RegistrationOverlayStateError,
} from '../registration_overlay/validateLti1p3RegistrationOverlayState'
import {type Lti1p3RegistrationOverlayState} from '../registration_overlay/Lti1p3RegistrationOverlayState'
import {LtiRegistrationUpdateRequestId} from '../model/lti_ims_registration/LtiRegistrationUpdateRequestId'
/**
 * Steps are:
 * 1. Open modal, prompting for url
 * 2. User enters url, clicks "register," and an iframe is inserted, pointed at /api/lti/register
 * 3. When the app responds with a postMessage done, the iframe is removed, and we show the
 *    created dev key along with the permissions it has
 */

/**
 * Actions for the dynamic registration modal
 */
export interface DynamicRegistrationActions {
  /**
   * Loads a registration token from the BE for the given account.
   * Changes states to keep track of the loading process.
   *
   * @param accountId The account id the tool is being registered for
   *    can be a account id, shard-relative id, or the string 'site_admin'
   * @param dynamicRegistrationUrl The url to use for dynamic registration
   * @param unifiedToolId Included in token if provided
   * @param updatingRegistrationId The id of the registration to update
   * @returns
   */
  loadRegistrationToken: (
    accountId: AccountId,
    dynamicRegistrationUrl: string,
    unifiedToolId?: UnifiedToolId,
    updatingRegistrationId?: LtiRegistrationId,
  ) => void

  /**
   * Loads an already existing registration from the backend and sets the state to 'reviewing'.
   *
   * @param accountId ID of the account the Lti::IMS::Registration is associated with
   * @param registrationId ID of the Lti::IMS::Registration to load
   */
  loadRegistration: (accountId: AccountId, registrationId: LtiRegistrationId) => void

  /**
   * Enables the developer key for the given registration
   * and closes the modal
   *
   * @param accountId The account id the tool is being registered for
   * @param registration The registration to enable
   * @param overlay The overlay to apply to the registration
   * @returns
   */
  enableAndClose: (
    accountId: AccountId,
    registrationId: LtiRegistrationId,
    adminNickname: string,
    onSuccess: (registrationId: LtiRegistrationId) => void,
  ) => Promise<unknown>

  updateAndClose: (
    accountId: AccountId,
    registrationId: LtiRegistrationId,
    adminNickname: string,
    onSuccess: (registrationId: LtiRegistrationId) => void,
  ) => Promise<unknown>

  applyRegistrationUpdateRequest: (
    accountId: AccountId,
    registrationId: LtiRegistrationId,
    registrationUpdateRequestId: LtiRegistrationUpdateRequestId,
    overlay: LtiConfigurationOverlay,
    onSuccess: () => void,
  ) => Promise<unknown>

  /**
   * Deletes the Developer Key for the given id
   * and closes the modal. The previous state must be a confirmation state.
   *
   * @param prevState The previous state of the modal
   * @param accountId The account id the tool is being registered for
   * @param registrationId The id of the registration to delete
   * @returns The result of the delete operation
   */
  deleteKey: (
    prevState: ReviewingStateType,
    accountId: AccountId,
    registrationId: LtiRegistrationId,
  ) => Promise<ApiResult<unknown>>
  /**
   * Transition to a new confirmation state, copying the
   * state from the previous confirmation state
   * Only applies the new state if the previous state
   * is currently active
   *
   * @param prevState The previous state
   * @param newState The new state
   */
  transitionToConfirmationState(
    prevState: ConfirmationStateType,
    newState: ConfirmationStateType,
    reviewing?: boolean,
  ): void
  transitionToReviewingState(prevState: ConfirmationStateType): void

  /**
   * Advance to the next step of the wizard
   * If there are errors, onErrors will be called with the list of errors
   * to be handled, and the state will not be advanced.
   * Otherwise, the state will be advanced to the next step
   */
  advanceStep(
    accountId: AccountId,
    onErrors: (errors: Array<Lti1p3RegistrationOverlayStateError>) => void,
    registrationId?: LtiRegistrationId,
    onSuccessfulRegistration?: (ltiRegistrationId: LtiRegistrationId) => void,
  ): void

  /**
   * Handle previous button click for the current step
   */
  previousStep(currentState: ConfirmationStateType): void

  /**
   * Returns true if the given step should be skipped
   */
  shouldSkipStep(step: ConfirmationStateType, currentState: DynamicRegistrationWizardState): boolean

  /**
   * Check if current step can proceed (used for button state)
   */
  canProceed(): boolean

  /**
   * Checks if current step is valid
   * Returns a list of ids of fields needing attention before
   * the wizard should proceed
   * If the list is empty, the wizard can proceed
   */
  validateStep(): Array<Lti1p3RegistrationOverlayStateError>

  /**
   * Handle the final save action from the reviewing screen
   */
  handleSave(
    accountId: AccountId,
    registrationId?: LtiRegistrationId,
    onSuccessfulRegistration?: (ltiRegistrationId: LtiRegistrationId) => void,
  ): void
}

/**
 * ADT for the state of the dynamic registration modal
 *
 * 'closed' means the modal is not open
 * 'opened' means the modal is open, and the user is entering a url
 * 'registering' means the modal is open, and the user has entered a url and clicked "register"
 *    and we are waiting for the app to respond with a postMessage
 */
export type DynamicRegistrationWizardState =
  | {
      _type: 'RequestingToken'
    }
  | {
      _type: 'WaitingForTool'
      registrationToken: DynamicRegistrationToken
    }
  | {
      _type: 'LoadingRegistration'
      registrationToken?: DynamicRegistrationToken
    }
  | {
      _type: 'Error'
      message: string
    }
  | ConfirmationState<'PermissionConfirmation'>
  | ConfirmationState<'PrivacyLevelConfirmation'>
  | ConfirmationState<'PlacementsConfirmation'>
  | ConfirmationState<'NamingConfirmation'>
  | ConfirmationState<'IconConfirmation'>
  | ConfirmationState<'Reviewing'>
  | ConfirmationState<'Enabling'>
  | ConfirmationState<'Updating'>
  | ConfirmationState<'DeletingDevKey'>

export type ConfirmationStateType = Exclude<
  DynamicRegistrationWizardState['_type'],
  'RequestingToken' | 'WaitingForTool' | 'LoadingRegistration' | 'Error'
>

type ReviewingStateType = Exclude<ConfirmationStateType, 'Enabling' | 'DeletingDevKey' | 'Updating'>

/**
 * A list of the review steps and their order.
 * Used for calculating the current step and which step
 * to advance to.
 */
const DynamicRegistrationWizardStepOrder: Array<ConfirmationStateType> = [
  'PermissionConfirmation',
  'PrivacyLevelConfirmation',
  'PlacementsConfirmation',
  'NamingConfirmation',
  'IconConfirmation',
  'Reviewing',
]

export const isReviewingState = (
  state: DynamicRegistrationWizardState,
): state is ConfirmationState<ReviewingStateType> => isReviewingStateType(state._type)

const isReviewingStateType = (
  stateType: DynamicRegistrationWizardState['_type'],
): stateType is ConfirmationStateType =>
  stateType.endsWith('Confirmation') || stateType === 'Reviewing'

/**
 * Helper for constructing a 'confirmation' state (a substate of the confirmation screen)
 */
export type ConfirmationState<Tag extends string> = {
  _type: Tag
  registration: LtiRegistrationWithConfiguration
  overlayStore: Lti1p3RegistrationOverlayStore
  reviewing: boolean
  hasSubmitted?: boolean
}

const errorState = (message: string): DynamicRegistrationWizardState => ({
  _type: 'Error',
  message,
})

/**
 * Wraps a value into an object with a state key, for use with Zustand
 * @param state
 * @returns
 */
const stateFor =
  <A>(state: A) =>
  () => ({state})

/**
 * Wraps a value into an object with a state key, for use with Zustand
 * @param state
 * @returns
 */
const stateForTag =
  <K extends DynamicRegistrationWizardState['_type']>(
    _type: K,
    value: Omit<Extract<DynamicRegistrationWizardState, {_type: K}>, '_type'>,
  ) =>
  () => ({state: {_type, ...value}})

/**
 * Helper for constructing a 'confirming' state
 * @param tag
 * @returns
 */
const confirmationState =
  <Tag extends ConfirmationStateType>(_type: Tag) =>
  (
    registration: LtiRegistrationWithConfiguration,
    overlayStore: Lti1p3RegistrationOverlayStore,
    reviewing = false,
    hasSubmitted = false,
  ): DynamicRegistrationWizardState => ({
    _type,
    registration,
    overlayStore,
    reviewing,
    hasSubmitted,
  })

const enabling = confirmationState('Enabling')
const updating = confirmationState('Updating')
const deleting = confirmationState('DeletingDevKey')

/**
 * Helper for constructing a state from another state.
 * The new state will only be applied if the current state has the same tag
 *
 * @example
 *
 * const state = stateFrom('PermissionConfirmation`)(state => {
 *   return state;
 * })
 *
 * @param tag
 * @returns
 */
const stateFrom =
  <T extends DynamicRegistrationWizardState['_type']>(_type: T) =>
  (
    mkNewState: (
      oldState: Extract<DynamicRegistrationWizardState, {_type: T}>,
      actions: DynamicRegistrationActions,
    ) => DynamicRegistrationWizardState,
  ) =>
  (oldState: {state: DynamicRegistrationWizardState} & DynamicRegistrationActions) => {
    if (oldState.state._type === _type) {
      return {
        state: mkNewState(
          oldState.state as Extract<DynamicRegistrationWizardState, {_type: T}>,
          oldState,
        ),
      }
    } else {
      return oldState
    }
  }

type StateUpdater = (
  updater: (s: {state: DynamicRegistrationWizardState} & DynamicRegistrationActions) => {
    state: DynamicRegistrationWizardState
  },
) => void

/**
 * Zustand store for the state of the dynamic registration modal
 */
export const mkUseDynamicRegistrationWizardState = (service: DynamicRegistrationWizardService) =>
  create<{state: DynamicRegistrationWizardState} & DynamicRegistrationActions>(
    (set: StateUpdater, get) => ({
      state: {_type: 'RequestingToken'},
      /**
       * Fetches a registration token from the dynamic registration URL
       * sets the state to 'registering'
       * and listens for a message from the tool
       *
       * @param accountId
       * @param dynamicRegistrationUrl
       * @param unifiedToolId included in token. optional.
       */
      loadRegistrationToken: (
        accountId: AccountId,
        dynamicRegistrationUrl: string,
        unifiedToolId?: UnifiedToolId,
        updatingRegistrationId?: LtiRegistrationId,
      ) => {
        set(stateFor({_type: 'RequestingToken'}))

        service
          .fetchRegistrationToken(
            accountId,
            dynamicRegistrationUrl,
            unifiedToolId,
            updatingRegistrationId,
          )
          .then(resp => {
            if (isSuccessful(resp)) {
              set(stateFor({_type: 'WaitingForTool', registrationToken: resp.data}))
              const onMessage = (message: MessageEvent) => {
                if (
                  message.data &&
                  typeof message.data.subject === 'string' &&
                  message.data.subject === 'org.imsglobal.lti.close' &&
                  message.origin === originOfUrl(dynamicRegistrationUrl)
                ) {
                  global.removeEventListener('message', onMessage)
                  set(
                    stateForTag('LoadingRegistration', {
                      registrationToken: resp.data,
                    }),
                  )

                  service.getRegistrationByUUID(accountId, resp.data.uuid).then(reg => {
                    if (isSuccessful(reg)) {
                      const store: Lti1p3RegistrationOverlayStore =
                        createLti1p3RegistrationOverlayStore(
                          reg.data.configuration,
                          reg.data.admin_nickname || reg.data.name,
                          reg.data.overlay?.data,
                        )
                      set(stateFor(confirmationState('PermissionConfirmation')(reg.data, store)))
                    } else {
                      set(stateFor(errorState(formatApiResultError(reg))))
                    }
                  })
                }
              }
              global.addEventListener('message', onMessage)
            } else {
              set(stateFor(errorState(formatApiResultError(resp))))
            }
          })
      },
      loadRegistration: async (accountId: AccountId, registrationId: LtiRegistrationId) => {
        set(stateFor({_type: 'LoadingRegistration'}))
        const reg = await service.fetchLtiRegistration(accountId, registrationId)

        if (isSuccessful(reg)) {
          const store: Lti1p3RegistrationOverlayStore = createLti1p3RegistrationOverlayStore(
            reg.data.configuration,
            reg.data.admin_nickname || reg.data.name,
            reg.data.overlay?.data,
          )

          set(stateFor(confirmationState('Reviewing')(reg.data, store, true)))
        } else {
          set(stateFor(errorState(formatApiResultError(reg))))
        }
      },
      enableAndClose: async (
        accountId: AccountId,
        registrationId: LtiRegistrationId,
        adminNickname: string,
        onSuccess: (registrationId: LtiRegistrationId) => void,
      ) => {
        set(
          stateFrom('Reviewing')(state => {
            const {overlay: convertedOverlay} = convertToLtiConfigurationOverlay(
              state.overlayStore.getState().state,
              state.registration.configuration,
            )

            service
              .updateRegistration({
                accountId,
                registrationId,
                overlay: convertedOverlay,
                adminNickname: state.overlayStore.getState().state.naming.nickname || adminNickname,
                workflowState: 'active',
              })
              .then(result => {
                if (isSuccessful(result)) {
                  onSuccess(registrationId)
                } else {
                  set(stateFor(errorState(formatApiResultError(result))))
                }
              })

            return enabling(state.registration, state.overlayStore)
          }),
        )
      },
      updateAndClose: async (
        accountId: AccountId,
        registrationId: LtiRegistrationId,
        adminNickname: string,
        onSuccess: (registrationId: LtiRegistrationId) => void,
      ) => {
        set(
          stateFrom('Reviewing')(state => {
            const {overlay: convertedOverlay} = convertToLtiConfigurationOverlay(
              state.overlayStore.getState().state,
              state.registration.configuration,
            )

            service
              .updateRegistration({
                accountId,
                registrationId,
                overlay: convertedOverlay,
                adminNickname: state.overlayStore.getState().state.naming.nickname || adminNickname,
                workflowState: 'active',
              })
              .then(result => {
                if (isSuccessful(result)) {
                  onSuccess(registrationId)
                } else {
                  set(stateFor(errorState(formatApiResultError(result))))
                }
              })

            return updating(state.registration, state.overlayStore)
          }),
        )
      },
      applyRegistrationUpdateRequest: async (
        accountId: AccountId,
        registrationId: LtiRegistrationId,
        registrationUpdateRequestId: LtiRegistrationUpdateRequestId,
        overlay: LtiConfigurationOverlay,
        onSuccess: () => void,
      ) => {
        set(stateFrom('Updating')(state => updating(state.registration, state.overlayStore)))

        const result = await service.applyLtiRegistrationUpdateRequest(
          accountId,
          registrationId,
          registrationUpdateRequestId,
          overlay,
        )

        if (isSuccessful(result)) {
          onSuccess()
        } else {
          set(stateFor(errorState(formatApiResultError(result))))
        }
      },
      deleteKey: async (
        prevState: ReviewingStateType,
        accountId: AccountId,
        registrationId: LtiRegistrationId,
      ) => {
        set(stateFrom(prevState)(state => deleting(state.registration, state.overlayStore)))
        const result = await service.deleteRegistration(accountId, registrationId)
        if (isUnsuccessful(result)) {
          set(stateFor(errorState(formatApiResultError(result))))
        }
        return result
      },
      transitionToConfirmationState: (
        prevState: ConfirmationStateType,
        newState: ConfirmationStateType,
        reviewing?: boolean,
      ) =>
        set(
          stateFrom(prevState)(a => ({
            ...a,
            reviewing: reviewing ?? a.reviewing,
            _type: newState,
          })),
        ),
      transitionToReviewingState: (prevState: ConfirmationStateType) =>
        set(
          stateFrom(prevState)(a => ({
            ...a,
            _type: 'Reviewing',
            reviewing: true,
          })),
        ),
      advanceStep: (accountId, onErrors, registrationId, onSuccessfulRegistration): void => {
        const currentState = get().state
        if (currentState._type === 'Reviewing') {
          get().handleSave(accountId, registrationId, onSuccessfulRegistration)
        } else if (isReviewingState(currentState)) {
          set(
            stateFrom(currentState._type)(state => {
              const errors = get().validateStep()
              if (errors.length > 0) {
                onErrors(errors)
                return state
              } else {
                const currentStepIndex = DynamicRegistrationWizardStepOrder.indexOf(state._type)
                // if we are at the last step, or we're reviewing, advance to review
                if (
                  currentStepIndex === DynamicRegistrationWizardStepOrder.length - 1 ||
                  state.reviewing
                ) {
                  return {
                    ...state,
                    reviewing: true,
                    _type: 'Reviewing',
                  }
                }

                let candidateStepIndex = currentStepIndex + 1
                while (candidateStepIndex < DynamicRegistrationWizardStepOrder.length) {
                  const candidateStep = DynamicRegistrationWizardStepOrder[candidateStepIndex]
                  if (get().shouldSkipStep(candidateStep, state)) {
                    candidateStepIndex++
                    continue
                  } else {
                    break
                  }
                }

                return {
                  ...state,
                  _type: candidateStepIndex
                    ? DynamicRegistrationWizardStepOrder[candidateStepIndex]
                    : state._type,
                }
              }
            }),
          )
        }
      },
      shouldSkipStep: (
        step: ConfirmationStateType,
        currentState: ConfirmationState<ConfirmationStateType>,
      ): boolean => {
        return false
      },
      previousStep: (currentStep: ConfirmationStateType) => {
        const currentState = get().state
        if (isReviewingState(currentState)) {
          set(
            stateFrom(currentState._type)(state => {
              const currentStepIndex = DynamicRegistrationWizardStepOrder.indexOf(currentStep)

              // starting at the current step, find the previous step in the order, skipping any that should be skipped
              let candidateStepIndex = currentStepIndex - 1
              while (candidateStepIndex > 0) {
                const candidateStep = DynamicRegistrationWizardStepOrder[candidateStepIndex]
                if (get().shouldSkipStep(candidateStep, state)) {
                  candidateStepIndex--
                  continue
                } else {
                  break
                }
              }

              const previousStep =
                candidateStepIndex >= 0
                  ? DynamicRegistrationWizardStepOrder[candidateStepIndex]
                  : DynamicRegistrationWizardStepOrder[0]

              return {
                ...state,
                _type: previousStep,
                reviewing: false,
              }
            }),
          )
        }
      },
      canProceed: () =>
        !['RequestingToken', 'WaitingForTool', 'LoadingRegistration'].includes(get().state._type),
      validateStep: () => {
        const currentState = get().state
        if (currentState._type === 'IconConfirmation') {
          return validateIconUris(currentState.overlayStore.getState().state.icons)
        } else {
          return []
        }
      },
      handleSave: (
        accountId: AccountId,
        registrationId?: LtiRegistrationId,
        onSuccessfulRegistration?,
      ) =>
        set(
          stateFrom('Reviewing')(state => {
            const {overlay: convertedOverlay} = convertToLtiConfigurationOverlay(
              state.overlayStore.getState().state,
              state.registration.configuration,
            )
            const adminNickname =
              state.overlayStore.getState().state.naming.nickname || state.registration.name

            if (registrationId) {
              // Update existing registration
              service
                .updateRegistration({
                  accountId,
                  registrationId,
                  overlay: convertedOverlay,
                  adminNickname,
                })
                .then(result => {
                  if (isSuccessful(result)) {
                    onSuccessfulRegistration?.(registrationId)
                  } else {
                    set(stateFor(errorState(formatApiResultError(result))))
                  }
                })
              return updating(state.registration, state.overlayStore)
            } else {
              // Enable new registration
              service
                .updateRegistration({
                  accountId,
                  registrationId: state.registration.id,
                  overlay: convertedOverlay,
                  adminNickname,
                  workflowState: 'active',
                })
                .then(result => {
                  if (isSuccessful(result)) {
                    onSuccessfulRegistration?.(state.registration.id)
                  } else {
                    set(stateFor(errorState(formatApiResultError(result))))
                  }
                })
              return enabling(state.registration, state.overlayStore)
            }
          }),
        ),
    }),
  )

const originOfUrl = (urlStr: string) => {
  const url = new URL(urlStr)
  return url.origin
}
